import { supabase, SUPABASE_ENABLED } from "./supabase";
import { posthog } from "./posthog";
import { getVisitorId } from "./visitor";

const ph = () => (typeof window !== "undefined" && (posthog as any).__loaded ? posthog : null);

const localFallback = (row: Record<string, unknown>, bucket: string) => {
  try {
    const k = `alvion.outbox.${bucket}`;
    const cur = JSON.parse(localStorage.getItem(k) || "[]");
    cur.push({ ...row, ts: Date.now() });
    localStorage.setItem(k, JSON.stringify(cur.slice(-200)));
  } catch {}
};

export async function trackBlockView(block: string, index: number) {
  ph()?.capture("block_viewed", { block_name: block, block_index: index });
}

export async function trackBlockDwell(block: string, dwellMs: number) {
  ph()?.capture("block_dwell", { block_name: block, dwell_ms: dwellMs });
}

export async function trackCtaClick(cta: string, block?: string, lang?: string) {
  ph()?.capture("cta_click", { cta_name: cta, block_name: block });
  const row = { visitor_id: getVisitorId(), cta_name: cta, block_name: block ?? null, lang: lang ?? null };
  if (!SUPABASE_ENABLED || !supabase) return localFallback(row, "cta");
  const { error } = await supabase.from("cta_clicks").insert(row);
  if (error) localFallback(row, "cta");
}

export async function trackSurveyStarted() {
  ph()?.capture("survey_started");
}

export async function upsertSurveyAnswer(opts: {
  answers: Record<string, unknown>;
  questionId: string;
  questionIndex: number;
  answer: unknown;
  completed: boolean;
  droppedAt: number | null;
  lang?: string;
}) {
  ph()?.capture("survey_answer", {
    question: opts.questionId,
    question_index: opts.questionIndex,
    answer: opts.answer,
  });
  if (opts.completed) ph()?.capture("survey_completed", { total: Object.keys(opts.answers).length });

  const row = {
    visitor_id: getVisitorId(),
    answers: opts.answers,
    completed: opts.completed,
    dropped_at_question: opts.droppedAt,
    lang: opts.lang ?? null,
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
  };
  if (!SUPABASE_ENABLED || !supabase) return localFallback(row, "survey");
  const { error } = await supabase
    .from("survey_responses")
    .upsert(row, { onConflict: "visitor_id" });
  if (error) localFallback(row, "survey");
}

export async function trackSurveyDropped(questionIndex: number) {
  ph()?.capture("survey_dropped", { dropped_at_question: questionIndex });
}

export async function submitWaitlist(form: {
  first: string;
  last: string;
  email: string;
  phone?: string;
  lang?: string;
}): Promise<{ ok: boolean; error?: string }> {
  ph()?.capture("early_access_signup", { email: form.email });
  ph()?.identify(getVisitorId(), { email: form.email, first_name: form.first, last_name: form.last });
  const row = {
    visitor_id: getVisitorId(),
    first_name: form.first,
    last_name: form.last,
    email: form.email,
    phone: form.phone || null,
    lang: form.lang ?? null,
  };
  if (!SUPABASE_ENABLED || !supabase) {
    localFallback(row, "waitlist");
    return { ok: true };
  }
  const { error } = await supabase.from("waitlist_signups").insert(row);
  if (error) {
    localFallback(row, "waitlist");
    return { ok: false, error: error.message };
  }
  return { ok: true };
}
